import { useWindowVirtualizer } from '@tanstack/react-virtual';
import {
  ADMIN_ADD_MANUAL_DELIVERY,
  AdminBrandsPicker,
  AdminDatePickerModal,
  AdminHeader,
  AdminLocationsPicker,
  AdminSelect,
  AdminSpinner,
  Div,
  ErrorToast,
  ExportIcon,
  ExportReportModal,
  H5,
  HeaderIconWrapper,
  Icon,
  ListSpinner,
  NotificationAlertWrapper,
  PlusIcon,
  QueryStatus,
  RefreshIcon,
  SearchInput,
  SimpleBrandEntity,
  SimpleLocationEntity,
  SimpleVDriveDeliveryEntity,
  SuccessToast,
  TooltipWrapper,
  api,
  assignDeliveryToAuthenticatedUser,
  createDeliveriesReportExport,
  deliveriesImage,
  fetchAndDownloadCsvFile,
  getDeliveriesReportExport,
  handleError,
  handleSetUrlSearchParams,
  isAdmin,
  isPartner,
  isProd,
  lightTheme,
  renderDateLabelForFileName,
  renderDateLabelWithToday,
  setTimeStartEnd,
  setTimeStartEndMonth,
  useDeliveries,
  useIsMobile,
  useReportExportSocket,
  virtualizerStyle,
  reportError,
  MerchantBillingNotices,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import debouce from 'lodash.debounce';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { vDriveDeliveryStatus } from '../../types/api/wallet.types';
import { ClaimDeliveryModal } from './components/AssignDispatcherModal';
import useDeliveriesSocket from './components/DeliveriesSocket';
import { DeliveriesTableHeader } from './components/DeliveriesTableHeader';
import { DeliveryComponent } from './components/DeliveryComponent';
import { NoLocationsWithVDrive } from './components/NoLocationsWithVDrive';
import { useDeliveriesInfiniteScroll } from './hooks/useDeliveriesInfiniteScroll';

const statusOptions = [
  { name: 'All Statuses', value: 'allStatuses' },
  { name: 'Pending Assign', value: vDriveDeliveryStatus.pending_assign },
  { name: 'Pending Merchant', value: vDriveDeliveryStatus.pending_merchant },
  { name: 'Scheduled', value: vDriveDeliveryStatus.scheduled },
  { name: 'Runner Assigned', value: vDriveDeliveryStatus.runner_assigned },
  { name: 'En Route to Pickup', value: vDriveDeliveryStatus.en_route_pickup },
  { name: 'Arrived at Pickup', value: vDriveDeliveryStatus.arrived_pickup },
  { name: 'Picked Up', value: vDriveDeliveryStatus.picked_up },
  { name: 'En Route to Dropoff', value: vDriveDeliveryStatus.en_route_dropoff },
  { name: 'Arrived at Dropoff', value: vDriveDeliveryStatus.arrived_dropoff },
  { name: 'Completed', value: vDriveDeliveryStatus.completed },
  { name: 'Cancelled', value: vDriveDeliveryStatus.cancelled },
  { name: 'Returned', value: vDriveDeliveryStatus.returned },
  { name: 'Unknown', value: vDriveDeliveryStatus.unknown },
];

const typeOptions = (
  <optgroup label="Select Status">
    {statusOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

export const Deliveries = () => {
  useDeliveriesSocket();
  const today = new Date();
  const navigate = useNavigate();

  const INITIAL_DATE = [
    setTimeStartEndMonth(today, 'start'),
    setTimeStartEndMonth(today, 'today'),
  ];

  const [dateInput, setDateInput] = useState<(Date | string)[]>(INITIAL_DATE);

  const {
    setDeliveries,
    hasNewDelivery,
    setHasNewDelivery,
    vDriveLocations,
    setVDriveLocations,
  } = useDeliveries((state) => state);

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedLocations, setSelectedLocations] = useState<
    SimpleLocationEntity[]
  >([]);
  const [selectedBrands, setSelectedBrands] = useState<SimpleBrandEntity[]>([]);

  const status = (searchParams.get('status') ?? null) as
    | vDriveDeliveryStatus
    | 'allStatuses'
    | null;
  const from = searchParams.get('from') ?? INITIAL_DATE[0];
  const to = searchParams.get('to') ?? INITIAL_DATE[1];
  const initialQuery = searchParams.get('query') || '';
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState<string | null>(initialQuery || null);
  const locationIdsParam = searchParams.get('locationIds');
  const brandIdsParam = searchParams.get('brandIds');
  const [claimDeliveryModal, setClaimDeliveryModal] = useState<number | null>(
    null
  );

  const [noVDriveLocationsModal, setNoVDriveLocationsModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const filters = {
    from,
    to,
    status,
    query,
    locationIdsParam,
    brandIdsParam,
  };

  const {
    deliveries: deliveriesFromHook,
    observerRef,
    isFetchingNextPage,
    isFetching,
    status: queryStatus,
    refetch,
    saveScrollPosition,
  } = useDeliveriesInfiniteScroll(filters);

  useEffect(() => {
    setDeliveries(deliveriesFromHook);
  }, [deliveriesFromHook, setDeliveries]);

  const setDate = (date: string[]) => {
    handleSetUrlSearchParams('from', date[0], setSearchParams);
    handleSetUrlSearchParams('to', date[1], setSearchParams);
  };

  const setStatus = (value: vDriveDeliveryStatus | 'allStatuses') => {
    if (value === 'allStatuses')
      return handleSetUrlSearchParams('status', null, setSearchParams);
    handleSetUrlSearchParams('status', value, setSearchParams);
  };

  const debouncedSearch = useMemo(
    () => debouce((value: string | null) => setQuery(value), 500),
    []
  );

  const handleDateChange = (date: Date[]) => {
    setDateInput(date);
  };

  const handleDateApply = () => {
    // setDeliveries([]);

    if (dateInput.length < 2) {
      setDate([
        setTimeStartEnd(dateInput[0], 'start'),
        setTimeStartEnd(dateInput[0], 'end'),
      ]);
      setDateInput([dateInput[0], dateInput[0]]);
    } else {
      setDate([
        setTimeStartEnd(dateInput[0], 'start'),
        setTimeStartEnd(dateInput[1], 'end'),
      ]);
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value || null);
    handleSetUrlSearchParams('query', value, setSearchParams);
  };

  async function getVDriveLocations() {
    try {
      const { data } = await api.get(`/v1/vdrive/locations`);
      setVDriveLocations(data.data);
    } catch (error) {
      reportError(error);
    }
  }

  // Load brands from URL when locations are loaded
  const handleLocationsLoaded = (loadedLocations: SimpleLocationEntity[]) => {
    const brandIds = brandIdsParam
      ? brandIdsParam
          .split(',')
          .map((id) => parseInt(id.trim()))
          .filter((id) => !isNaN(id))
      : [];

    if (brandIds.length > 0) {
      const brandsFromLocations: SimpleBrandEntity[] = [];

      // Find brands in loaded locations
      brandIds.forEach((brandId) => {
        for (const location of loadedLocations) {
          const brand = location.brands.find((b) => b.id === brandId);
          if (brand) {
            brandsFromLocations.push(brand);
            break;
          }
        }
      });

      setSelectedBrands(brandsFromLocations);
    }
  };

  useEffect(() => {
    if (isPartner) getVDriveLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartner]);

  const handleLocationsSubmit = (
    _locationIds?: number[],
    locationObjects?: SimpleLocationEntity[]
  ) => {
    handleSetUrlSearchParams('brandIds', null, setSearchParams);
    setSelectedBrands([]);
    if (locationObjects) setSelectedLocations(locationObjects);
  };

  const handleBrandsSubmit = () => {
    // URL is updated by picker; hook refetches when brandIdsParam changes
  };

  const deliveriesRefresh = () => {
    setHasNewDelivery(false);
    refetch();
  };

  const currentExportIdRef = useRef<number | null>(null);

  const { observe: observeReportExport } = useReportExportSocket(
    async (payload) => {
      if (currentExportIdRef.current !== payload.reportExportId) return;
      observeReportExport(payload.reportExportId, false);
      try {
        const { data } = await getDeliveriesReportExport(
          payload.reportExportId
        );
        const detail = data.data;
        if (payload.fail || detail.status === 'failed') {
          ErrorToast(detail.errorMessage || 'Export failed');
        } else if (detail.downloadUrl) {
          await fetchAndDownloadCsvFile(
            detail.downloadUrl,
            `Deliveries Report ${renderDateLabelForFileName(
              from.toString(),
              to.toString()
            )}.csv`
          );
        }
      } catch (error) {
        ErrorToast(handleError(error));
      } finally {
        currentExportIdRef.current = null;
        setExportLoading(false);
      }
    }
  );

  async function handleExportReport(includePhysicalLocationsOnly: boolean) {
    try {
      setExportLoading(true);
      const locationIds = selectedLocations.map((l) => l.id);
      const brandIds = selectedBrands.map((b) => b.id);
      const { data } = await createDeliveriesReportExport({
        from: DateTime.fromISO(from.toString()).toISODate() || '',
        to: DateTime.fromISO(to.toString()).toISODate() || '',
        locationIds: locationIds.length > 0 ? locationIds : undefined,
        brandIds:
          isAdmin && !includePhysicalLocationsOnly && brandIds.length > 0
            ? brandIds
            : undefined,
        status: status && status !== 'allStatuses' ? status : undefined,
        query: query || undefined,
        includePhysicalLocationsOnly: includePhysicalLocationsOnly || undefined,
      });
      currentExportIdRef.current = data.data.id;
      observeReportExport(data.data.id, true);
    } catch (error) {
      ErrorToast(handleError(error));
      setExportLoading(false);
    }
  }
  const [selectedDelivery, setSelectedDelivery] =
    useState<SimpleVDriveDeliveryEntity | null>(null);

  function handleOnManualClick() {
    if (isAdmin) {
      return navigate(ADMIN_ADD_MANUAL_DELIVERY);
    }
    if (isPartner && vDriveLocations.length > 0) {
      return navigate(ADMIN_ADD_MANUAL_DELIVERY);
    } else {
      return setNoVDriveLocationsModal(true);
    }
  }

  const ManualDeliveryButton = () => (
    <Div onClick={handleOnManualClick}>
      <HeaderIconWrapper
        $opened={false}
        $disabled={isPartner && vDriveLocations.length === 0}
      >
        <PlusIcon />
      </HeaderIconWrapper>
    </Div>
  );

  const handleAssignToAuthenticatedUser = async (id: number) => {
    if (!id) return;
    try {
      const { data } = await assignDeliveryToAuthenticatedUser(id);
      SuccessToast(data.message);
      refetch();
      setClaimDeliveryModal(null);
    } catch (error) {
      reportError(error);
    }
  };

  const isMobile = useIsMobile();
  const listRef = useRef<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    setScrollMargin(listRef.current?.offsetTop ?? 0);
  }, [deliveriesFromHook.length]);

  const rowVirtualizer = useWindowVirtualizer({
    count: deliveriesFromHook.length,
    estimateSize: () => (isMobile ? 168 : 82),
    overscan: 3,
    scrollMargin,
  });

  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [isMobile, rowVirtualizer]);

  useLayoutEffect(() => {
    if (deliveriesFromHook.length === 0) return;
    const saved = sessionStorage.getItem('admin-deliveries-scroll');
    if (saved !== null) {
      const scrollY = parseInt(saved, 10);
      if (!Number.isNaN(scrollY)) {
        sessionStorage.removeItem('admin-deliveries-scroll');
        window.scrollTo({ top: scrollY });
      }
    }
  }, [deliveriesFromHook.length]);

  return (
    <>
      <AdminHeader title={'Deliveries'}>
        <Div $flex $alignCenter $gap="20px" $gapSM="10px">
          {!(isPartner && isProd) && (
            <TooltipWrapper tooltip="Export Report">
              <HeaderIconWrapper
                $opened={false}
                onClick={() => setExportModal(true)}
              >
                <Icon $size="24" color={lightTheme.primary}>
                  <ExportIcon />
                </Icon>
              </HeaderIconWrapper>
            </TooltipWrapper>
          )}

          <NotificationAlertWrapper $show={hasNewDelivery}>
            <HeaderIconWrapper $opened={false} onClick={deliveriesRefresh}>
              <RefreshIcon />
            </HeaderIconWrapper>
          </NotificationAlertWrapper>
          <AdminDatePickerModal
            mode="range"
            value={dateInput}
            onChange={handleDateChange as any}
            click={handleDateApply}
          />
          <SearchInput
            value={inputValue}
            maxWidth="350px"
            maxWidthSM="120px"
            placeholder="Search"
            onChange={onSearchChange}
          />
        </Div>
      </AdminHeader>

      {/* The merchant's billing banners, as on the tablet; nothing in admin. */}
      <MerchantBillingNotices />

      <Div $mobile $flex $between $alignCenter $mt="32" $mtSM="12">
        <H5 $mb="0">{renderDateLabelWithToday(from, to)}</H5>
        <ManualDeliveryButton />
      </Div>

      <Div
        $justifyEnd
        $flex
        $between
        $alignCenter
        $gap="32px"
        $gapSM="8px"
        $columnSM
        $m="32px 0"
        $mSM="12px 0 20px 0"
      >
        <Div $width={isAdmin ? '33%' : '50%'} $widthSM="100%">
          <AdminLocationsPicker
            selectedLocations={selectedLocations}
            setSelectedLocations={setSelectedLocations}
            handleSubmit={() => handleLocationsSubmit()}
            persistToUrl={true}
            onLocationsLoaded={handleLocationsLoaded}
            setSearchParams={setSearchParams}
          />
        </Div>
        {isAdmin && (
          <Div $width="33%" $widthSM="100%">
            <AdminBrandsPicker
              selectedLocations={selectedLocations}
              selectedBrans={selectedBrands}
              setSelectedBrands={setSelectedBrands}
              handleSubmit={() => handleBrandsSubmit()}
              persistToUrl={true}
              setSearchParams={setSearchParams}
            />
          </Div>
        )}
        <Div $width={isAdmin ? '33%' : '50%'} $widthSM="100%">
          <AdminSelect
            value={status ?? ''}
            options={typeOptions}
            onChange={({ target }: { target: HTMLSelectElement }) => {
              setStatus(target.value as vDriveDeliveryStatus | 'allStatuses');
            }}
            $shadowDesign
          />
        </Div>
      </Div>

      <Div
        $flex
        $alignCenter
        $between
        $columnSM
        $gap="32px"
        $gapSM="20px"
        $m="32px 0"
        $mSM="20px 0"
        $desktop
      >
        <Div>
          <H5 $mb="0">{renderDateLabelWithToday(from, to)}</H5>
        </Div>
        <ManualDeliveryButton />
      </Div>

      {deliveriesFromHook.length > 0 && <DeliveriesTableHeader />}

      <Div ref={listRef}>
        <Div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const delivery = deliveriesFromHook[virtualRow.index];
            if (!delivery) return null;
            const virtualizerStyleValue = virtualizerStyle(
              virtualRow,
              rowVirtualizer
            );
            return (
              <Div
                key={delivery.id ?? virtualRow.index}
                style={virtualizerStyleValue as any}
              >
                <DeliveryComponent
                  delivery={delivery}
                  onBeforeNavigate={saveScrollPosition}
                  handleClaimDelivery={setClaimDeliveryModal}
                />
              </Div>
            );
          })}
        </Div>
      </Div>

      {deliveriesFromHook.length === 0 &&
        queryStatus !== QueryStatus.PENDING &&
        !isFetching && (
          <NoDataComponent
            image={deliveriesImage}
            title="No Deliveries"
            description="No results found for the selected filters."
          />
        )}

      <ListSpinner loading={isFetchingNextPage} observerRef={observerRef} />

      {((isFetching && !isFetchingNextPage) || exportLoading) && (
        <AdminSpinner />
      )}

      <NoLocationsWithVDrive
        isOpen={noVDriveLocationsModal}
        setIsOpen={setNoVDriveLocationsModal}
      />

      <ClaimDeliveryModal
        modalIsOpen={claimDeliveryModal}
        closeModal={() => setClaimDeliveryModal(null)}
        handleClaimDelivery={handleAssignToAuthenticatedUser}
      />

      <ExportReportModal
        isOpen={exportModal}
        closeModal={() => setExportModal(false)}
        onConfirm={handleExportReport}
        dateLabel={renderDateLabelWithToday(from, to)}
        selectedLocations={selectedLocations}
        selectedBrands={isAdmin ? selectedBrands : []}
        statusLabel={
          status && status !== 'allStatuses'
            ? statusOptions.find((o) => o.value === status)?.name ?? null
            : null
        }
        query={query}
      />
    </>
  );
};
